import React, { useEffect, useState } from 'react';
import { Formik, Form, FormikErrors, FormikTouched } from 'formik';
import * as Yup from 'yup';
import { Col, Row } from 'reactstrap';
import { BaseSelect, MultiSelect } from '../../Components/Base/BaseSelect';
import BaseInput from '../../Components/Base/BaseInput';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import { createJobCard, getCustomerList, getProductList, getTechnicianList, updateJobCard, viewJobCard } from 'Api/jobcard';
import BaseButton from 'Components/Base/BaseButton';
import {
  InputPlaceHolder,
  SelectPlaceHolder,
  validationMessages,
} from "../../Components/constants/validation";
import { REPORTED_ISSUE, JOB_DESCRIPTION, CUSTOMER_NAME, TECHNICIAN, WARRANTY_START_DATE, WARRANTY_END_DATE, START_DATE, END_DATE, PRIORITY_LEVEL, PRODUCT_CODE, QUANTITY, RATE, AVAILABLE_QUANTITY, AMOUNT, OPTIONS, ENGINE_NO_1, ENGINE_NO_2, ENGINE_NO_3, CHASSIS_NO } from '../../Components/constants/JobCard'
import Loader from 'Components/Base/BaseLoader';
import { checkStatusCodeSuccess } from 'Components/constants/Common';
import { CustomerIdLabel, CustomerNameLabel } from 'Components/constants/Staff';

interface Customer {
  id: number;
  name: string;
  user_draft_id: string;
}

interface Product {
  id: number;
  product_code: string;
  batch_no: number;
  rate: number;
  AvailableQuantity: number;
  quantity: number;
}

interface Technician {
  id: number;
  technician_name: string;
}

interface JobFormValues {
  reportedIssue: string;
  jobDescription: string;
  customerId: string;
  customerName: string;
  warrantyStartDate: string;
  warrantyEndDate: string;
  technicians: number[];
  startDate: string;
  completionDate: string;
  priorityLevel: string;
  engineNo1: string;
  engineNo2: string;
  engineNo3: string;
  chassisNo: string;
  products: {
    productCode: string;
    AvailableQuantity: number;
    quantity: number;
    rate: number;
    amount: number;
  }[];
}

const validationSchema = Yup.object({
  reportedIssue: Yup.string().required(validationMessages.required('Reported Issue')),
  jobDescription: Yup.string().required(validationMessages.required('Job Description')),
  customerId: Yup.string().required(validationMessages.required('Customer Name')),
  warrantyStartDate: Yup.date().required(validationMessages.required('Warranty Start Date')).nullable(),
  warrantyEndDate: Yup.date().required(validationMessages.required('Warranty End Date')).nullable()
    .min(Yup.ref('warrantyStartDate'), validationMessages.greaterThan('Warranty Start Date', 'Warranty End Date')),
  startDate: Yup.date().required(validationMessages.required('Start Date')).nullable(),
  completionDate: Yup.date().required(validationMessages.required('Completion Date')).nullable()
    .min(Yup.ref('startDate'), validationMessages.greaterThan('Completion Date', 'Start Date')),
  priorityLevel: Yup.string().required(validationMessages.required('Priority Level')),
  technicians: Yup.array().min(1, validationMessages.atLeastOneSelected('Technician')),
  products: Yup.array().of(
    Yup.object().shape({
      productCode: Yup.string().required(validationMessages.required('Product Code')),
      quantity: Yup.number().min(1, validationMessages.positiveNumber(QUANTITY))
        .required(validationMessages.required(QUANTITY))
        .test("quantity-less-than-available", "Quantity must be less than available quantity", function (value: any) {
          const { AvailableQuantity } = this.parent;
          return value <= AvailableQuantity;
        }),
      rate: Yup.number().min(0, validationMessages.minLength('Rate', 0)).required(validationMessages.required('Rate')),
      amount: Yup.number().min(0, validationMessages.minLength('Amount', 0)).required(validationMessages.required('Amount')),
    })
  ).required(validationMessages.atLeastOneSelected('Product')),
});

const JobForm: React.FC = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [technicians, setTechnicians] = useState<Technician[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [initialValues, setInitialValues] = useState<JobFormValues | null>(null);
  const navigate = useNavigate();
  const { jobId } = useParams<{ jobId: string }>();
  const [btnLoader, setBtnLoader] = useState<boolean>(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [customerRes, productRes, technicianRes] = await Promise.all([
          getCustomerList(),
          getProductList(),
          getTechnicianList()
        ]);

        setCustomers(customerRes.data);
        setProducts(productRes.data);
        setTechnicians(technicianRes.data);

        const jobData = jobId ? await viewJobCard(jobId) : null;

        setInitialValues({
          reportedIssue: jobData?.reported_issue || '',
          jobDescription: jobData?.job_description || '',
          customerId: jobData?.authUser?.id || '',
          customerName: jobData?.authUser?.name || '',
          warrantyStartDate: jobData?.warranty_start_date || '',
          warrantyEndDate: jobData?.warranty_end_date || '',
          technicians: jobData ? jobData.technician_ids.split(',').map(Number) : [],
          startDate: jobData?.start_date || '',
          completionDate: jobData?.completion_date || '',
          priorityLevel: jobData?.priority_level || '',
          engineNo1: jobData?.engine_no_1 || '',
          engineNo2: jobData?.engine_no_2 || '',
          engineNo3: jobData?.engine_no_2 || '',
          chassisNo: jobData?.chassis_no || '',
          products: jobData ? jobData.jobCardProduct.map((item: any) => ({
            productCode: item.product.id,
            AvailableQuantity: item.product.quantity,
            quantity: item.quantity,
            rate: item.rate,
            amount: item.total_amount,
          })) : [{ productCode: '', AvailableQuantity: '', quantity: null, rate: null, amount: 0 }],
        });

        setLoading(false);
      } catch (error) {}
    };

    fetchData();
  }, [jobId]);

  if (loading || !initialValues) {
    return <Loader />;
  }

  const customerList = customers.map((customer) => ({
    label: customer.user_draft_id,
    value: customer.id,
  }));

  const getAvailableProducts = (index: number, values: JobFormValues) => {
    const selectedProductCodes = values.products
      .map((product: any, idx: any) => (idx !== index ? product.productCode : null))
      .filter(Boolean); // Filter out null values

    return products.filter(product => !selectedProductCodes.includes(product.id));
  };

  return (
    <div className="page-contnet" style={{ marginTop: '80px' }}>
      <div className="flex-grow-1 mx-3">
        <div className="d-flex justify-content-between align-items-center ">
          <span className="fs-4">{jobId ? 'Update Job Card' : 'Add Job Card'}</span>
          <div className="d-flex align-items-center gap-2">
            <BaseButton
              className="btn btn-danger"
              onClick={() => navigate('/job-card')}
              children={<><i className="mdi mdi-arrow-left" /></>}
            />
          </div>
        </div>
        <div className="container-fluid bg-white p-4 rounded shadow my-1">
          <Formik<JobFormValues>
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={async (values, { setSubmitting }) => {
              setSubmitAttempted(true);
              setBtnLoader(true);
              const payload = {
                products: values.products.map(product => ({
                  product_id: product.productCode,
                  AvailableQuantity: product.AvailableQuantity,
                  quantity: Number(product.quantity),
                  rate: product.rate,
                  total_amount: product.amount,
                })),
                auth_id: values.customerId,
                technician_ids: values.technicians,
                reported_issue: values.reportedIssue,
                job_description: values.jobDescription,
                warranty_start_date: values.warrantyStartDate,
                warranty_end_date: values.warrantyEndDate,
                start_date: values.startDate,
                completion_date: values.completionDate,
                priority_level: values.priorityLevel,
                engine_no_1: values.engineNo1,
                engine_no_2: values.engineNo2,
                engine_no_3: values.engineNo3,
                chassis_no: values.chassisNo,
                status: "In Progress"
              };

              try {
                setBtnLoader(true);
                if (jobId) {
                  updateJobCard(jobId, payload).then((resp) => {
                    if (checkStatusCodeSuccess(resp.statusCode)) {
                      setBtnLoader(false);
                      toast.success('Job updated successfully!');
                      navigate('/job-card');
                    }
                  });
                } else {
                  createJobCard(payload).then((resp) => {
                    if (checkStatusCodeSuccess(resp.statusCode)) {
                      toast.success('Job added successfully!');
                      setBtnLoader(false);
                      navigate('/job-card');
                    }
                  });
                }
              } catch (error) {
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({ values, setFieldValue, handleBlur, errors, handleChange, touched, isSubmitting }) => {
              const showError = (
                fieldName: string,
                touchedFields: FormikTouched<JobFormValues>,
                errors: FormikErrors<JobFormValues>
              ) => {
                if (!submitAttempted) return undefined;

                const fieldNameParts = fieldName.split('.');
                let touchedValue: any = touchedFields;
                let errorValue: any = errors;

                for (const part of fieldNameParts) {
                  touchedValue = touchedValue?.[part];
                  errorValue = errorValue?.[part];
                  if (touchedValue === undefined || errorValue === undefined) break;
                }

                return touchedValue && errorValue ? errorValue : undefined;
              };

              const handleAddRow = () => {
                setFieldValue('products', [
                  ...values.products,
                  { productCode: '', AvailableQuantity: null, quantity: null, rate: null, amount: null },
                ]);
              };

              const handleRemoveRow = (index: number) => {
                const newProducts = [...values.products];
                if (newProducts.length > 1) {
                  newProducts.splice(index, 1);
                  setFieldValue('products', newProducts);
                }
              };

              return (
                <Form className="form-responsive">
                  <Row className="mb-3">
                    <Col lg={3} md={6} sm={12}>
                      <BaseInput
                        label={REPORTED_ISSUE}
                        name="reportedIssue"
                        value={values.reportedIssue}
                        onChange={(e) => setFieldValue('reportedIssue', e.target.value)}
                        handleBlur={handleBlur}
                        error={showError('reportedIssue', touched, errors)}
                        touched={touched.reportedIssue}
                        placeholder={InputPlaceHolder(REPORTED_ISSUE)}
                        required
                      />
                    </Col>
                    <Col lg={3} md={6} sm={12}>
                      <BaseInput
                        label={JOB_DESCRIPTION}
                        name="jobDescription"
                        type="textarea"
                        value={values.jobDescription}
                        onChange={(e) => setFieldValue('jobDescription', e.target.value)}
                        handleBlur={handleBlur}
                        error={showError('jobDescription', touched, errors)}
                        touched={touched.jobDescription}
                        placeholder={InputPlaceHolder(JOB_DESCRIPTION)}
                        required
                      />
                    </Col>
                    <Col lg={3} md={6} sm={12}>
                      <BaseSelect
                        name="customerId"
                        label={CustomerIdLabel}
                        placeholder={SelectPlaceHolder(CUSTOMER_NAME)}
                        options={customerList}
                        error={showError('customerId', touched, errors)}
                        touched={touched.customerId}
                        value={values.customerId}
                        handleChange={(name: string, value: string) => {
                          setFieldValue(name, value);
                          const selectedCustomer = customers.find(c => c.id === Number(value));
                          setFieldValue('customerName', selectedCustomer ? selectedCustomer.name : '');
                          
                          if (value) {
                            setFieldValue('customerId', value);
                          }
                        }}
                        handleBlur={handleBlur}
                        required
                      />
                    </Col>

                    <Col lg={3} md={6} sm={12}>
                      <BaseInput
                        label={CustomerNameLabel}
                        name="customerName"
                        value={values.customerName}
                        readOnly
                        handleBlur={handleBlur}
                        placeholder={InputPlaceHolder(CUSTOMER_NAME)}
                        disabled
                      />
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col lg={3} md={6} sm={12}>
                      <label htmlFor="technician">{TECHNICIAN}</label>
                      <MultiSelect
                        name="technicians"
                        placeholder={SelectPlaceHolder(TECHNICIAN)}
                        isMulti={true}
                        value={technicians.filter((tech) =>
                          values.technicians.includes(tech.id)
                        ).map((tech) => ({
                          label: tech.technician_name,
                          value: tech.id,
                        }))}
                        onChange={(selectedOptions: any) => {
                          setFieldValue(
                            'technicians',
                            selectedOptions ? selectedOptions.map((option: any) => option.value) : []
                          );
                        }}
                        options={technicians.map((tech) => ({
                          label: tech.technician_name,
                          value: tech.id,
                        }))}
                        touched={touched.technicians}
                        error={showError('technicians', touched, errors)}
                      />
                    </Col>
                    <Col lg={3} md={6} sm={12}>
                      <BaseInput
                        label={WARRANTY_START_DATE}
                        name="warrantyStartDate"
                        type="date"
                        placeholder={WARRANTY_START_DATE}
                        value={values.warrantyStartDate}
                        onChange={(e) => setFieldValue('warrantyStartDate', e.target.value)}
                        handleBlur={handleBlur}
                        error={showError('warrantyStartDate', touched, errors)}
                        touched={touched.warrantyStartDate}
                        required
                      />
                    </Col>
                    <Col lg={3} md={6} sm={12}>
                      <BaseInput
                        label={WARRANTY_END_DATE}
                        placeholder={WARRANTY_END_DATE}
                        name="warrantyEndDate"
                        type="date"
                        value={values.warrantyEndDate}
                        onChange={(e) => setFieldValue('warrantyEndDate', e.target.value)}
                        handleBlur={handleBlur}
                        error={showError('warrantyEndDate', touched, errors)}
                        touched={touched.warrantyEndDate}
                        required
                      />
                    </Col>
                    <Col lg={3} md={6} sm={12}>
                      <BaseInput
                        label={START_DATE}
                        placeholder={InputPlaceHolder(START_DATE)}
                        name="startDate"
                        type="date"
                        value={values.startDate}
                        onChange={(e) => setFieldValue('startDate', e.target.value)}
                        handleBlur={handleBlur}
                        error={showError('startDate', touched, errors)}
                        touched={touched.startDate}
                        required
                      />
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col lg={3} md={6} sm={12}>
                      <BaseInput
                        label={END_DATE}
                        placeholder={InputPlaceHolder(END_DATE)}
                        name="completionDate"
                        type="date"
                        value={values.completionDate}
                        onChange={(e) => setFieldValue('completionDate', e.target.value)}
                        handleBlur={handleBlur}
                        error={showError('completionDate', touched, errors)}
                        touched={touched.completionDate}
                        required
                      />
                    </Col>

                    <Col lg={3} md={6} sm={12}>
                      <BaseSelect
                        name="priorityLevel"
                        placeholder={SelectPlaceHolder(PRIORITY_LEVEL)}
                        label={PRIORITY_LEVEL}
                        options={OPTIONS}
                        value={values.priorityLevel}
                        handleChange={(name: any, value: any) => setFieldValue(name, value)}
                        handleBlur={handleBlur}
                        error={showError('priorityLevel', touched, errors)}
                        touched={touched.priorityLevel}
                        required
                      />
                    </Col>
                    <Col lg={3} md={6} sm={12}>
                      <BaseInput
                        label={ENGINE_NO_1}
                        name="engineNo1"
                        value={values.engineNo1}
                        onChange={(e) => setFieldValue('engineNo1', e.target.value)}
                        handleBlur={handleBlur}
                        placeholder={ENGINE_NO_1}
                      />
                    </Col>
                    <Col lg={3} md={6} sm={12}>
                      <BaseInput
                        type='string'
                        label={ENGINE_NO_2}
                        name="engineNo2"
                        value={values.engineNo2}
                        onChange={(e) => setFieldValue('engineNo2', e.target.value)}
                        handleBlur={handleBlur}
                        touched={touched.engineNo2}
                        placeholder={ENGINE_NO_2}
                      />
                    </Col>
                  </Row>

                  <Row className='mb-3'>
                    <Col lg={3} md={6} sm={12}>
                      <BaseInput
                        label={ENGINE_NO_3}
                        name="engineNo3"
                        value={values.engineNo3}
                        onChange={(e) => setFieldValue('engineNo3', e.target.value)}
                        handleBlur={handleBlur}
                        placeholder={InputPlaceHolder(ENGINE_NO_3)}
                      />
                    </Col>
                    <Col lg={3} md={6} sm={12}>
                      <BaseInput
                        label={CHASSIS_NO}
                        name="chassisNo"
                        value={values.chassisNo}
                        onChange={(e) => setFieldValue('chassisNo', e.target.value)}
                        handleBlur={handleBlur}
                        placeholder={InputPlaceHolder(CHASSIS_NO)}
                      />
                    </Col>
                  </Row>
                  <hr></hr>
                  <div className="container-fluid p-0">
                    {values.products.map((product, index) => (
                      <div className={index % 2 === 0 ? "row g-3 mb-4 bg-wheat p-2" : "row g-3 mb-4 p-2"} key={index}>
                        <div className="col-12 col-sm-6 col-lg-3">
                          <BaseSelect
                            name={`products.${index}.productCode`}
                            label={PRODUCT_CODE}
                            placeholder={SelectPlaceHolder(PRODUCT_CODE)}
                            options={getAvailableProducts(index, values).map((product) => ({
                              label: `${product.product_code} (${product.batch_no})`,
                              value: product.id,
                            }))}
                            value={product.productCode}
                            handleChange={(name: any, value: any) => {
                              setFieldValue(name, value);
                              const selectedProduct = products.find(prod => prod.id === value);
                              if (selectedProduct) {
                                setFieldValue(`products.${index}.rate`, selectedProduct.rate);
                                setFieldValue(`products.${index}.AvailableQuantity`, selectedProduct.quantity);
                                setFieldValue(`products.${index}.amount`, product.quantity * selectedProduct.rate);
                              }
                              // Remove validation error immediately on selection
                              if (value) {
                                setFieldValue(`products.${index}.productCode`, value);
                              }
                            }}
                            handleBlur={handleBlur}
                            error={showError(`products.${index}.productCode`, touched, errors)}
                            touched={touched.products?.[index]?.productCode}
                            required
                          />
                        </div>

                        <div className="col-12 col-sm-6 col-lg-3">
                          <BaseInput
                            label={AVAILABLE_QUANTITY}
                            name={`products[${index}].AvailableQuantity`}
                            value={product.AvailableQuantity}
                            readOnly
                            handleBlur={handleBlur}
                            placeholder={InputPlaceHolder(AVAILABLE_QUANTITY)}
                            disabled
                          />
                        </div>

                        <div className="col-12 col-sm-6 col-lg-3">
                          <BaseInput
                            label={QUANTITY}
                            placeholder={InputPlaceHolder(QUANTITY)}
                            name={`products[${index}].quantity`}
                            type="number"
                            value={product.quantity}
                            onChange={(e) => {
                              const quantity = e.target.value;
                              setFieldValue(`products[${index}].quantity`, quantity);
                              const rate = product.rate;
                              setFieldValue(`products[${index}].amount`, Number(quantity) * rate);
                            }}
                            handleBlur={handleBlur}
                            error={showError(`products.${index}.quantity`, touched, errors)}
                            touched={touched.products?.[index]?.productCode || false}
                            required
                          />
                        </div>

                        <div className="col-12 col-sm-6 col-lg-3">
                          <BaseInput
                            label={RATE}
                            placeholder={InputPlaceHolder(RATE)}
                            name={`products[${index}].rate`}
                            type="number"
                            value={product.rate}
                            onChange={(e) => {
                              const rate = Number(e.target.value);
                              setFieldValue(`products[${index}].rate`, rate);
                              const quantity = product.quantity;
                              setFieldValue(`products[${index}].amount`, rate * quantity);
                            }}
                            handleBlur={handleBlur}
                            disabled
                          />
                        </div>

                        <div className="col-12 col-sm-6 col-lg-3">
                          <BaseInput
                            label={AMOUNT}
                            name={`products[${index}].amount`}
                            type="number"
                            value={product.amount}
                            handleBlur={handleBlur}
                            disabled={true}
                          />
                        </div>

                        <div className="col-12 col-sm-6 col-lg-3 d-flex align-items-end">
                          {index > 0 && (
                              <i className="mdi mdi-delete-outline fs-4 text-danger" onClick={() => handleRemoveRow(index)}></i>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  <Row className="mb-3">
                    <Col lg={12} className="d-flex justify-content-end">
                      <BaseButton
                        className="btn btn-primary"
                        onClick={handleAddRow}
                        children="Add Product"
                      />
                    </Col>
                  </Row>

                  <Row className="mb-5">
                    <Col lg={12} className="d-flex justify-content-end">
                      <BaseButton
                        className="btn btn-success me-2"
                        type="submit"
                        children="Submit"
                        label={jobId ? 'Update' : 'Submit'}
                        loader={btnLoader}
                        onClick={() => {
                          if (!isSubmitting) {
                            setSubmitAttempted(true);
                          }
                        }}
                      />
                      <BaseButton
                        className="btn btn-danger"
                        onClick={() => navigate('/job-card')}
                        label="Cancel"
                      />
                    </Col>
                  </Row>
                </Form>
              );
            }}
          </Formik>
        </div>
      </div>
    </div>
  )
};

export default JobForm;